import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, ROLE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Printer, FileText, IdentificationCard, Bank, Briefcase, House,
  ShieldCheck, Plus, Trash, ArrowSquareOut,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SECTION = ({ icon: Icon, title, children }) => (
  <div className="bg-[#0D0D10] border border-[#2D2D35] rounded-md p-4">
    <div className="text-[10px] uppercase tracking-[0.3em] text-[#E50914] mb-3 inline-flex items-center gap-1.5">
      <Icon size={12} weight="fill" /> {title}
    </div>
    <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">{children}</div>
  </div>
);

const KV = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className="text-sm text-zinc-200 font-mono mt-0.5 break-words">
      {value === 0 || value ? String(value) : "—"}
    </div>
  </div>
);

export default function EmployeeProfileDialog({ open, onOpenChange, employeeId, isAdmin }) {
  const [tab, setTab] = useState("profile");
  const [busy, setBusy] = useState(false);
  // new custom field def
  const [newDef, setNewDef] = useState({ key: "", label: "", field_type: "TEXT", description: "" });

  // Bundle all four API loads into a single state update to satisfy strict
  // react-hooks linting (single setState in useEffect).
  const [bundle, setBundle] = useState(null); // { emp, templates, fieldDefs, empValues }

  useEffect(() => {
    if (!open) { setBundle(null); return; }
    if (!employeeId) return;
    (async () => {
      try {
        const empReq = api.get(`/employees/${employeeId}`);
        const tReq   = isAdmin ? api.get("/hr-documents/templates") : Promise.resolve({ data: [] });
        const fReq   = isAdmin ? api.get("/hr-documents/custom-fields") : Promise.resolve({ data: [] });
        const vReq   = isAdmin ? api.get(`/employees/${employeeId}/custom-fields`) : Promise.resolve({ data: {} });
        const [e, t, fd, v] = await Promise.all([empReq, tReq, fReq, vReq]);
        setBundle({ emp: e.data, templates: t.data || [], fieldDefs: fd.data || [], empValues: v.data || {} });
      } catch (er) { toast.error(formatApiError(er)); }
    })();
  }, [open, employeeId, isAdmin]);

  const emp = bundle?.emp || null;
  const templates = bundle?.templates || [];
  const fieldDefs = bundle?.fieldDefs || [];
  const empValues = bundle?.empValues || {};
  function setFieldDefs(updater) {
    setBundle((b) => ({ ...b, fieldDefs: typeof updater === "function" ? updater(b.fieldDefs) : updater }));
  }
  function setEmpValues(updater) {
    setBundle((b) => ({ ...b, empValues: typeof updater === "function" ? updater(b.empValues) : updater }));
  }

  const groupedTemplates = useMemo(() => {
    const g = {};
    templates.forEach((t) => { (g[t.category] = g[t.category] || []).push(t); });
    return g;
  }, [templates]);

  function dlPdf(url, filename) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    if (filename) a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  }

  function token() {
    return localStorage.getItem("rwms_token") || "";
  }

  async function openAuthed(url) {
    // Fetch with bearer auth then open via blob (since URL doesn't carry headers)
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      window.open(u, "_blank");
      // delay revoke to give browser time to load
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch (e) { toast.error(`Could not download: ${e.message || e}`); }
  }

  async function generateTemplate(t) {
    if (busy) return;
    setBusy(true);
    await openAuthed(`${API}/employees/${employeeId}/hr-document/${t.id}`);
    setBusy(false);
  }

  async function printProfile() {
    if (busy) return;
    setBusy(true);
    await openAuthed(`${API}/employees/${employeeId}/profile/pdf`);
    setBusy(false);
  }

  async function saveCustomValues() {
    try {
      await api.patch(`/employees/${employeeId}/custom-fields`, { values: empValues });
      toast.success("Custom values saved");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function addFieldDef() {
    if (!newDef.label.trim()) { toast.error("Label is required"); return; }
    if (!newDef.key.trim()) { newDef.key = newDef.label.trim().toLowerCase().replace(/\s+/g, "_"); }
    try {
      const { data } = await api.post("/hr-documents/custom-fields", newDef);
      setFieldDefs((prev) => {
        const next = prev.filter((f) => f.id !== data.id);
        next.push(data);
        return next;
      });
      setNewDef({ key: "", label: "", field_type: "TEXT", description: "" });
      toast.success("Field added");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function delFieldDef(id) {
    if (!window.confirm("Delete this custom field definition? Existing values on employees are kept.")) return;
    try {
      await api.delete(`/hr-documents/custom-fields/${id}`);
      setFieldDefs((prev) => prev.filter((f) => f.id !== id));
      toast.success("Removed");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (!emp) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white max-w-md">
          <DialogHeader><DialogTitle>Loading…</DialogTitle></DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white max-w-5xl max-h-[92vh] overflow-y-auto"
                     data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="font-display text-2xl">{emp.name}</div>
              <div className="text-xs text-zinc-400 mt-0.5">
                {emp.designation || "—"} · {emp.department || "—"}
              </div>
              <div className="text-[11px] font-mono text-zinc-500 mt-1">
                EID <span className="text-white">{emp.employee_id}</span> ·
                Verif <span className="text-[#E50914]">{emp.verification_id || "—"}</span>
              </div>
            </div>
            <span className={`text-[10px] px-2 py-1 rounded ${
              emp.active ? "bg-[#22C55E]/15 text-[#22C55E]" : "bg-zinc-700/30 text-zinc-400"
            }`}>{emp.active ? "ACTIVE" : "INACTIVE"}</span>
            {isAdmin && (
              <Button size="sm" onClick={printProfile} disabled={busy}
                      data-testid="print-profile-btn"
                      className="bg-[#E50914] hover:bg-[#C80812] h-8 text-xs">
                <Printer size={14} className="mr-1.5" /> Print profile
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-3">
          <TabsList className="bg-[#0D0D10] border border-[#2D2D35]" data-testid="profile-tabs">
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
            {isAdmin && <TabsTrigger value="documents" data-testid="tab-documents">HR Documents</TabsTrigger>}
            {isAdmin && <TabsTrigger value="custom" data-testid="tab-custom">Custom Fields</TabsTrigger>}
          </TabsList>

          {/* PROFILE TAB */}
          <TabsContent value="profile" className="mt-4 space-y-3">
            <SECTION icon={IdentificationCard} title="Personal & Contact">
              <KV label="Email" value={emp.email} />
              <KV label="Phone" value={emp.contact} />
              <KV label="Emergency contact" value={emp.emergency_contact} />
              <KV label="Date of birth" value={(emp.date_of_birth || "").slice(0, 10)} />
              <KV label="Gender" value={emp.gender} />
              <KV label="Nationality" value={emp.nationality} />
            </SECTION>

            <SECTION icon={House} title="Address">
              <KV label="Street" value={emp.address} />
              <KV label="City" value={emp.city} />
              <KV label="State" value={emp.state} />
              <KV label="Country" value={emp.country} />
              <KV label="Pin code" value={emp.pin_code} />
            </SECTION>

            {isAdmin && (
              <SECTION icon={ShieldCheck} title="Identity (Sensitive — Admin only)">
                <KV label="Aadhaar" value={emp.aadhaar} />
                <KV label="PAN" value={emp.pan} />
                <KV label="Passport" value={emp.passport} />
                <KV label="Driving license" value={emp.driving_license} />
                <KV label="Voter ID" value={emp.voter_id} />
                <KV label="Verification ID" value={emp.verification_id} />
              </SECTION>
            )}

            {isAdmin && (
              <SECTION icon={Bank} title="Bank (Admin only)">
                <KV label="Bank name" value={emp.bank_name} />
                <KV label="Account holder" value={emp.account_holder} />
                <KV label="Account number" value={emp.bank_account} />
                <KV label="IFSC" value={emp.ifsc} />
                <KV label="Branch" value={emp.branch} />
                <KV label="UPI ID" value={emp.upi_id} />
              </SECTION>
            )}

            <SECTION icon={Briefcase} title="Employment">
              <KV label="Role" value={ROLE_LABELS?.[emp.role] || emp.role} />
              <KV label="Designation" value={emp.designation} />
              <KV label="Department" value={emp.department} />
              <KV label="Reporting manager" value={emp.reporting_manager} />
              <KV label="Employment type" value={emp.employment_type} />
              <KV label="Current status" value={emp.current_status} />
              <KV label="Shift" value={emp.shift} />
              <KV label="Joining date" value={(emp.joining_date || "").slice(0, 10)} />
            </SECTION>

            {isAdmin && (
              <SECTION icon={FileText} title="Payroll Defaults (Admin only)">
                <KV label="Monthly salary" value={`₹ ${Number(emp.monthly_salary || 0).toLocaleString()}`} />
                <KV label="Working days/mo" value={emp.monthly_working_days} />
                <KV label="Daily hours" value={emp.daily_working_hours} />
                <KV label="Method" value={(emp.salary_calc_method || "FULL_DAY").replaceAll("_", " ")} />
                <KV label="Daily assigned jobs" value={emp.daily_assigned_jobs} />
                <KV label="Min required jobs" value={emp.min_required_jobs} />
                <KV label="Perf. ded / miss" value={`₹ ${emp.perf_deduction_per_missing_job || 0}`} />
                <KV label="Perf. bonus / extra" value={`₹ ${emp.perf_bonus_per_extra_job || 0}`} />
              </SECTION>
            )}

            {!!Object.keys(empValues || {}).length && (
              <SECTION icon={FileText} title="Custom Fields">
                {Object.entries(empValues).map(([k, v]) => (
                  <KV key={k} label={k.replaceAll("_", " ")} value={v} />
                ))}
              </SECTION>
            )}
          </TabsContent>

          {/* HR DOCUMENTS TAB */}
          {isAdmin && (
            <TabsContent value="documents" className="mt-4 space-y-4">
              <div className="text-xs text-zinc-500 leading-relaxed">
                Generate prefilled HR documents for <span className="text-white">{emp.name}</span>.
                Data is pulled live from the employee profile + custom fields. Documents are
                auto-stamped with Date, Employee ID, and Verification ID.
              </div>
              {Object.entries(groupedTemplates).map(([cat, list]) => (
                <div key={cat}>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                    {cat}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {list.map((t) => (
                      <div key={t.id} className="bg-[#0D0D10] border border-[#2D2D35] rounded p-4 flex gap-3"
                           data-testid={`doc-card-${t.id}`}>
                        <FileText size={28} className="text-[#E50914] shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm">{t.name}</div>
                          <div className="text-[11px] text-zinc-500 mt-1 leading-snug">
                            {t.description}
                          </div>
                          <Button onClick={() => generateTemplate(t)} disabled={busy} size="sm"
                                  data-testid={`generate-${t.id}`}
                                  className="mt-3 h-8 text-xs bg-[#E50914] hover:bg-[#C80812]">
                            <ArrowSquareOut size={12} className="mr-1.5" />
                            {busy ? "Generating…" : "Download PDF"}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>
          )}

          {/* CUSTOM FIELDS TAB */}
          {isAdmin && (
            <TabsContent value="custom" className="mt-4 space-y-5">
              <div className="bg-[#0D0D10] border border-[#2D2D35] rounded p-4">
                <div className="text-[10px] uppercase tracking-widest text-[#E50914] mb-3">
                  Add a new custom field (organisation-wide)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Label</Label>
                    <Input value={newDef.label} onChange={(e) => setNewDef({ ...newDef, label: e.target.value })}
                           placeholder="e.g. Blood Group"
                           data-testid="cf-label"
                           className="mt-2 bg-[#16161C] border-[#2D2D35]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Key</Label>
                    <Input value={newDef.key}
                           onChange={(e) => setNewDef({ ...newDef, key: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                           placeholder="auto"
                           data-testid="cf-key"
                           className="mt-2 bg-[#16161C] border-[#2D2D35]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Type</Label>
                    <Select value={newDef.field_type} onValueChange={(v) => setNewDef({ ...newDef, field_type: v })}>
                      <SelectTrigger className="mt-2 bg-[#16161C] border-[#2D2D35]" data-testid="cf-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                        {["TEXT", "NUMBER", "DATE", "TEXTAREA"].map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={addFieldDef} data-testid="cf-add"
                            className="bg-[#E50914] hover:bg-[#C80812] w-full">
                      <Plus size={14} className="mr-1" /> Add
                    </Button>
                  </div>
                </div>
                {!!fieldDefs.length && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {fieldDefs.map((f) => (
                      <div key={f.id}
                           className="text-[11px] bg-[#16161C] border border-[#2D2D35] rounded px-2 py-1 flex items-center gap-2">
                        <span className="text-zinc-300">{f.label}</span>
                        <span className="text-zinc-500 font-mono">({f.field_type})</span>
                        <button onClick={() => delFieldDef(f.id)} className="text-[#FF3B30] hover:text-white"
                                data-testid={`cf-del-${f.key}`}>
                          <Trash size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#E50914] mb-3">
                  Values for {emp.name}
                </div>
                {fieldDefs.length === 0 && (
                  <div className="text-xs text-zinc-500">
                    No custom fields defined yet. Add one above — it will become available
                    for all employees and gets included in the Joining Form / Profile PDF.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {fieldDefs.map((f) => (
                    <div key={f.id}>
                      <Label className="text-xs uppercase tracking-widest text-zinc-500">
                        {f.label} {f.required && <span className="text-[#E50914]">*</span>}
                      </Label>
                      {f.field_type === "TEXTAREA" ? (
                        <Textarea
                          value={empValues[f.key] ?? ""}
                          onChange={(e) => setEmpValues({ ...empValues, [f.key]: e.target.value })}
                          rows={3}
                          data-testid={`cf-val-${f.key}`}
                          className="mt-2 bg-[#0D0D10] border-[#2D2D35]" />
                      ) : (
                        <Input
                          type={f.field_type === "NUMBER" ? "number" : f.field_type === "DATE" ? "date" : "text"}
                          value={empValues[f.key] ?? ""}
                          onChange={(e) => setEmpValues({ ...empValues, [f.key]: e.target.value })}
                          data-testid={`cf-val-${f.key}`}
                          className="mt-2 bg-[#0D0D10] border-[#2D2D35]" />
                      )}
                      {f.description && (
                        <div className="text-[10px] text-zinc-500 mt-1">{f.description}</div>
                      )}
                    </div>
                  ))}
                </div>
                {!!fieldDefs.length && (
                  <div className="mt-4 flex justify-end">
                    <Button onClick={saveCustomValues} data-testid="cf-save"
                            className="bg-[#E50914] hover:bg-[#C80812]">
                      Save custom values
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
